/* ==========================================================================
   Zenith — "Ascent" motion system
   GSAP + ScrollTrigger + Lenis.

   Design rules this file follows:
   - Nothing is hidden by CSS until JS has confirmed GSAP is actually alive
     (the `ascent-motion` class arms the reveal base state). A CDN failure
     degrades to a plain, fully visible page rather than a blank one.
   - Every desktop-only affordance (cursor, magnetic pull, 3D tilt, pinning)
     is gated behind gsap.matchMedia so it is never even built on mobile.
   - prefers-reduced-motion short-circuits to a composed static state.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  var hasGSAP = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------- */
  /* Failsafe: whatever happens below, the page must become visible.         */
  /* ---------------------------------------------------------------------- */
  function releasePage() {
    root.classList.remove('zenith-show-intro');
    var intro = document.getElementById('ascent-intro');
    if (intro) intro.style.display = 'none';
  }

  window.setTimeout(function () {
    if (root.classList.contains('zenith-show-intro')) releasePage();
  }, 6000);

  /* ---------------------------------------------------------------------- */
  /* Static fallback path — reduced motion, or GSAP unavailable.             */
  /* ---------------------------------------------------------------------- */
  if (!hasGSAP || reduced) {
    releasePage();
    settleCountersStatically();
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  var lenis = null;

  /* ====================================================================== */
  /* Smooth scroll                                                          */
  /* ====================================================================== */
  function initSmoothScroll() {
    if (typeof window.Lenis === 'undefined') return;

    lenis = new Lenis({
      duration: 1.05,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      // Native momentum on touch beats emulated smoothing — leave it alone.
      syncTouch: false
    });

    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
  }

  function lockScroll(locked) {
    if (lenis) { locked ? lenis.stop() : lenis.start(); }
    root.style.overflow = locked ? 'hidden' : '';
  }

  /* --------------------------------------------------------------------- */
  /* theme.js locks the page with `body.style.overflow = 'hidden'` when the  */
  /* cart drawer, lightbox, or mobile nav opens. Lenis drives scroll on its  */
  /* own rAF loop and ignores that, so the background would keep gliding     */
  /* underneath. Watch the attribute instead of editing theme.js.            */
  /* --------------------------------------------------------------------- */
  function bridgeOverlayScrollLocks() {
    if (!lenis || typeof MutationObserver === 'undefined') return;

    new MutationObserver(function () {
      if (document.body.style.overflow === 'hidden') {
        lenis.stop();
      } else if (!root.classList.contains('zenith-show-intro')) {
        lenis.start();
      }
    }).observe(document.body, { attributes: true, attributeFilter: ['style'] });
  }

  /* ====================================================================== */
  /* 1. Cinematic intro                                                     */
  /* ====================================================================== */
  function initIntro(done) {
    var intro = document.getElementById('ascent-intro');

    if (!intro || !root.classList.contains('zenith-show-intro')) {
      releasePage();
      done();
      return;
    }

    var strokes  = intro.querySelectorAll('.ascent-intro__mark [data-draw]');
    var wordmark = intro.querySelector('.ascent-intro__wordmark');
    var stage    = intro.querySelector('.ascent-intro__stage');
    var aperture = intro.querySelector('.ascent-intro__aperture');
    var page     = Array.prototype.filter.call(document.body.children, function (el) {
      return el !== intro;
    });
    var hold = (parseInt(intro.getAttribute('data-hold'), 10) || 900) / 1000;

    lockScroll(true);

    // Prime each stroke with its own length so the draw reads as a single
    // continuous gesture rather than every path finishing at once.
    strokes.forEach(function (el) {
      var len = el.getTotalLength ? el.getTotalLength() : 300;
      gsap.set(el, { strokeDasharray: len, strokeDashoffset: len });
    });

    gsap.set(page, { opacity: 0 });

    var tl = gsap.timeline({
      onComplete: function () {
        intro.style.display = 'none';
        root.classList.remove('zenith-show-intro');
        lockScroll(false);
        ScrollTrigger.refresh();
        done();
      }
    });

    tl.to(strokes, {
        strokeDashoffset: 0,
        duration: 1.1,
        ease: 'power2.inOut',
        stagger: 0.14
      })
      .to(wordmark, { opacity: 1, duration: 0.6, ease: 'power2.out' }, '-=0.35')
      // Hold — let the mark sit before it moves.
      .to({}, { duration: hold })
      // The peak opens up and the homepage is revealed through it.
      .to(wordmark, { opacity: 0, duration: 0.4, ease: 'power2.in' })
      .to(aperture, {
        clipPath: 'circle(140% at 50% 42%)',
        duration: 1.15,
        ease: 'power3.inOut'
      }, '<')
      .to(stage, {
        scale: 14,
        opacity: 0,
        duration: 1.15,
        ease: 'power3.inOut',
        transformOrigin: '50% 42%'
      }, '<')
      .to(page, { opacity: 1, duration: 0.7, ease: 'power2.out' }, '-=0.75')
      .set(intro, { autoAlpha: 0 });
  }

  /* ====================================================================== */
  /* 2. Hero — masked line reveal, gold sweep, cursor tilt, parallax        */
  /* ====================================================================== */
  function initHero() {
    var hero = document.querySelector('[data-ascent-hero]');
    if (!hero) return;

    var lines = hero.querySelectorAll('.ascent-line > span');
    var tilt  = hero.querySelector('[data-tilt]');
    var sweep = hero.querySelector('.ascent-hero__sweep');
    var bg    = hero.querySelector('.ascent-hero__bg');
    var rest  = hero.querySelectorAll('[data-hero-fade]');

    var tl = gsap.timeline({ delay: 0.15 });

    if (lines.length) {
      gsap.set(lines, { yPercent: 118 });
      tl.to(lines, {
        yPercent: 0,
        duration: 1.15,
        ease: 'expo.out',
        stagger: 0.09
      });
    }

    if (tilt) {
      gsap.set(tilt, { opacity: 0, y: 40, scale: 0.96 });
      tl.to(tilt, { opacity: 1, y: 0, scale: 1, duration: 1.4, ease: 'expo.out' }, 0.1);
    }

    // The gold rake crossing the case, matched to the ad creative.
    if (sweep) {
      tl.fromTo(sweep,
        { opacity: 0, xPercent: -125 },
        { opacity: 1, xPercent: 125, duration: 1.6, ease: 'power2.inOut' },
        0.85
      ).to(sweep, { opacity: 0, duration: 0.3 }, '-=0.3');
    }

    if (rest.length) {
      gsap.set(rest, { opacity: 0, y: 20 });
      tl.to(rest, { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', stagger: 0.08 }, 0.55);
    }

    // Background parallax on scroll.
    if (bg) {
      gsap.to(bg, {
        yPercent: 12,
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
      });
    }

    // 3D tilt — desktop, fine pointer only.
    if (tilt) {
      var mm = gsap.matchMedia();
      mm.add('(min-width: 900px) and (pointer: fine)', function () {
        var rotX = gsap.quickTo(tilt, 'rotationX', { duration: 0.9, ease: 'power3.out' });
        var rotY = gsap.quickTo(tilt, 'rotationY', { duration: 0.9, ease: 'power3.out' });
        var posX = gsap.quickTo(tilt, 'x', { duration: 1.1, ease: 'power3.out' });
        var posY = gsap.quickTo(tilt, 'y', { duration: 1.1, ease: 'power3.out' });

        function onMove(e) {
          var nx = (e.clientX / window.innerWidth) * 2 - 1;   // -1 .. 1
          var ny = (e.clientY / window.innerHeight) * 2 - 1;
          rotY(nx * 9);
          rotX(-ny * 7);
          posX(nx * 14);
          posY(ny * 10);
        }

        function onLeave() { rotY(0); rotX(0); posX(0); posY(0); }

        window.addEventListener('mousemove', onMove, { passive: true });
        document.addEventListener('mouseleave', onLeave);

        return function () {
          window.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseleave', onLeave);
          gsap.set(tilt, { rotationX: 0, rotationY: 0, x: 0, y: 0 });
        };
      });
    }
  }

  /* ====================================================================== */
  /* 3. Material story — pinned engineering deep-dive                       */
  /* ====================================================================== */
  function initStories() {
    var stories = document.querySelectorAll('[data-ascent-story]');
    if (!stories.length) return;

    var mm = gsap.matchMedia();

    stories.forEach(function (story) {
      var pin       = story.querySelector('.ascent-story__pin');
      var img       = story.querySelector('.ascent-story__img');
      var blueprint = story.querySelector('.ascent-story__blueprint');
      var rake      = story.querySelector('.ascent-story__rake');
      var regs      = story.querySelectorAll('.ascent-story__reg');
      var caption   = story.querySelector('.ascent-story__caption');
      var rows      = story.querySelectorAll('.ascent-spec__row');
      var bar       = story.querySelector('.ascent-story__progress i');
      var copy      = story.querySelectorAll('[data-story-copy]');

      // The markup ships the real figure so a JS failure still shows a
      // correct spec sheet. Now that motion is confirmed, zero them out
      // so they have somewhere to count up from.
      rows.forEach(function (row) {
        var v = row.querySelector('[data-count-to]');
        if (v) v.textContent = '0';
      });

      /* -- Desktop: pin the visual and scrub the whole sequence ---------- */
      mm.add('(min-width: 900px)', function () {
        var tl = gsap.timeline({
          scrollTrigger: {
            trigger: pin,
            start: 'top top',
            end: '+=180%',
            pin: true,
            scrub: 0.8,
            anticipatePin: 1,
            onUpdate: function (self) {
              if (bar) gsap.set(bar, { height: (self.progress * 100).toFixed(2) + '%' });
            }
          }
        });

        if (copy.length) {
          gsap.set(copy, { opacity: 0, y: 28 });
          tl.to(copy, { opacity: 1, y: 0, duration: 0.6, stagger: 0.12, ease: 'power2.out' }, 0);
        }

        // Macro zoom into the weave.
        if (img) tl.fromTo(img, { scale: 1.02 }, { scale: 1.85, ease: 'none', duration: 3 }, 0);

        // Blueprint overlay resolves over the texture.
        if (blueprint) {
          tl.to(blueprint, { opacity: 1, duration: 0.5, ease: 'power1.out' }, 0.5)
            .fromTo(blueprint, { backgroundPosition: '0px 0px' },
                    { backgroundPosition: '96px 96px', ease: 'none', duration: 2.5 }, 0.5);
        }

        if (regs.length) tl.to(regs, { opacity: 1, duration: 0.35, stagger: 0.07 }, 0.75);
        if (caption)     tl.to(caption, { opacity: 1, duration: 0.4 }, 0.9);

        // Simulated light raking across the weave.
        if (rake) {
          tl.fromTo(rake,
            { opacity: 0, xPercent: -60 },
            { opacity: 1, xPercent: 60, ease: 'none', duration: 2 }, 0.6)
            .to(rake, { opacity: 0, duration: 0.4 }, 2.6);
        }

        // Spec rows resolve one at a time, each firing its own counter.
        rows.forEach(function (row, i) {
          var value = row.querySelector('[data-count-to]');
          gsap.set(row, { opacity: 0, y: 18 });
          tl.to(row, {
            opacity: 1, y: 0, duration: 0.4, ease: 'power2.out',
            onStart: function () { runCounter(value); }
          }, 1.1 + i * 0.35);
        });

        return function () {
          gsap.set([copy, rows], { clearProps: 'all' });
        };
      });

      /* -- Mobile: no pinning. Simple staged reveals, counters on entry. -- */
      mm.add('(max-width: 899px)', function () {
        gsap.set([blueprint, regs, caption], { opacity: 1 });

        var fired = false;
        function revealStory() {
          if (fired) return;
          fired = true;
          gsap.fromTo(copy, { opacity: 0, y: 22 },
            { opacity: 1, y: 0, duration: 0.7, stagger: 0.1, ease: 'power3.out' });
          gsap.fromTo(rows, { opacity: 0, y: 16 },
            { opacity: 1, y: 0, duration: 0.5, stagger: 0.12, ease: 'power2.out' });
          rows.forEach(function (row) { runCounter(row.querySelector('[data-count-to]')); });
        }

        var st = ScrollTrigger.create({
          trigger: story,
          start: 'top 72%',
          once: true,
          onEnter: revealStory
        });

        // If the page loaded already scrolled past this section (scroll
        // restoration, back-navigation, a deep link) no enter crossing will
        // ever happen, so reveal it outright instead of stranding it.
        if (st.progress > 0) revealStory();

        return function () { st.kill(); };
      });
    });
  }

  /* ====================================================================== */
  /* 6. Animated stat counters                                              */
  /* ====================================================================== */
  function runCounter(el) {
    if (!el || el.dataset.counted === '1') return;
    el.dataset.counted = '1';

    var target   = parseFloat(el.getAttribute('data-count-to'));
    var decimals = parseInt(el.getAttribute('data-count-decimals'), 10) || 0;
    if (isNaN(target)) return;

    var obj = { v: 0 };
    gsap.to(obj, {
      v: target,
      duration: 1.5,
      ease: 'power2.out',
      onUpdate: function () {
        el.textContent = obj.v.toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        });
      }
    });
  }

  // Used by the static fallback path so numbers are never left at zero.
  function settleCountersStatically() {
    document.querySelectorAll('[data-count-to]').forEach(function (el) {
      var target   = parseFloat(el.getAttribute('data-count-to'));
      var decimals = parseInt(el.getAttribute('data-count-decimals'), 10) || 0;
      if (isNaN(target)) return;
      el.textContent = target.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    });
  }

  /* ====================================================================== */
  /* 4 + 7. Magnetic pull for cards and buttons                             */
  /* ====================================================================== */
  function initMagnetic() {
    var mm = gsap.matchMedia();

    mm.add('(min-width: 1024px) and (pointer: fine)', function () {
      var targets = document.querySelectorAll('[data-magnetic]');
      var cleanups = [];

      targets.forEach(function (el) {
        var strength = parseFloat(el.getAttribute('data-magnetic')) || 0.28;
        var xTo = gsap.quickTo(el, 'x', { duration: 0.7, ease: 'power3.out' });
        var yTo = gsap.quickTo(el, 'y', { duration: 0.7, ease: 'power3.out' });
        var sTo = gsap.quickTo(el, 'scale', { duration: 0.5, ease: 'power3.out' });

        function move(e) {
          var r = el.getBoundingClientRect();
          xTo((e.clientX - (r.left + r.width / 2)) * strength);
          yTo((e.clientY - (r.top + r.height / 2)) * strength);
        }
        function enter() { sTo(1.02); }
        function leave() { xTo(0); yTo(0); sTo(1); }

        el.addEventListener('mousemove', move);
        el.addEventListener('mouseenter', enter);
        el.addEventListener('mouseleave', leave);

        cleanups.push(function () {
          el.removeEventListener('mousemove', move);
          el.removeEventListener('mouseenter', enter);
          el.removeEventListener('mouseleave', leave);
          gsap.set(el, { x: 0, y: 0, scale: 1 });
        });
      });

      return function () { cleanups.forEach(function (fn) { fn(); }); };
    });
  }

  /* ====================================================================== */
  /* 5. Custom cursor                                                       */
  /* ====================================================================== */
  function initCursor() {
    var mm = gsap.matchMedia();

    mm.add('(min-width: 1024px) and (pointer: fine)', function () {
      var ring = document.querySelector('.ascent-cursor');
      var dot  = document.querySelector('.ascent-cursor__dot');
      if (!ring || !dot) return;

      root.classList.add('ascent-cursor-on');

      var rx = gsap.quickTo(ring, 'x', { duration: 0.42, ease: 'power3.out' });
      var ry = gsap.quickTo(ring, 'y', { duration: 0.42, ease: 'power3.out' });
      var dx = gsap.quickTo(dot,  'x', { duration: 0.10, ease: 'power2.out' });
      var dy = gsap.quickTo(dot,  'y', { duration: 0.10, ease: 'power2.out' });

      function move(e) { rx(e.clientX); ry(e.clientY); dx(e.clientX); dy(e.clientY); }

      // Delegated so links inside sections rendered later still register.
      var hoverSel = 'a, button, [data-cursor-hover], input[type="submit"], summary';

      function over(e) {
        if (e.target.closest && e.target.closest(hoverSel)) {
          gsap.to(ring, { scale: 2.15, borderColor: 'rgba(201,162,75,0.55)', duration: 0.4, ease: 'power3.out' });
          gsap.to(dot, { scale: 0, duration: 0.3 });
        }
      }
      function out(e) {
        if (e.target.closest && e.target.closest(hoverSel)) {
          gsap.to(ring, { scale: 1, borderColor: 'rgba(201,162,75,1)', duration: 0.4, ease: 'power3.out' });
          gsap.to(dot, { scale: 1, duration: 0.3 });
        }
      }

      window.addEventListener('mousemove', move, { passive: true });
      document.addEventListener('mouseover', over);
      document.addEventListener('mouseout', out);

      return function () {
        root.classList.remove('ascent-cursor-on');
        window.removeEventListener('mousemove', move);
        document.removeEventListener('mouseover', over);
        document.removeEventListener('mouseout', out);
      };
    });
  }

  /* ====================================================================== */
  /* Generic scroll reveals                                                 */
  /* ====================================================================== */
  function initReveals() {
    root.classList.add('ascent-motion');

    var items = document.querySelectorAll('[data-ascent-reveal]');
    if (!items.length) return;

    ScrollTrigger.batch(items, {
      start: 'top 88%',
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 0.95,
          ease: 'expo.out',
          stagger: 0.09,
          overwrite: true
        });
      }
    });

    // Same stranding risk as the story sections: anything sitting entirely
    // above the viewport on load never gets an enter crossing. It cannot be
    // mid-animation either, so it is always safe to just show it.
    function showStrandedReveals() {
      items.forEach(function (el) {
        if (el.getBoundingClientRect().bottom < 0) {
          gsap.set(el, { opacity: 1, y: 0 });
        }
      });
    }
    showStrandedReveals();
    window.addEventListener('load', showStrandedReveals);

    // Gold rules draw themselves in.
    document.querySelectorAll('.ascent__rule').forEach(function (rule) {
      gsap.fromTo(rule, { scaleX: 0 }, {
        scaleX: 1, duration: 1.2, ease: 'expo.out',
        scrollTrigger: { trigger: rule, start: 'top 92%', once: true }
      });
    });
  }

  /* ====================================================================== */
  /* 7. Page transitions — soft veil instead of a hard reload flash         */
  /* ====================================================================== */
  function initPageTransitions() {
    var veil = document.querySelector('.ascent-veil');
    if (!veil) return;

    // Fade the veil out on arrival (covers bfcache restores too).
    function reveal() {
      gsap.set(veil, { visibility: 'visible' });
      gsap.to(veil, {
        opacity: 0, duration: 0.5, ease: 'power2.out',
        onComplete: function () { gsap.set(veil, { visibility: 'hidden' }); }
      });
    }
    gsap.set(veil, { opacity: 0, visibility: 'hidden' });
    window.addEventListener('pageshow', function (e) { if (e.persisted) reveal(); });

    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a');
      if (!link) return;

      // Bail on anything that isn't a plain same-tab, same-origin navigation.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;
      if (link.getAttribute('data-no-transition') !== null) return;

      var href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return;

      var url;
      try { url = new URL(link.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      e.preventDefault();
      gsap.set(veil, { visibility: 'visible' });
      gsap.to(veil, {
        opacity: 1,
        duration: 0.36,
        ease: 'power2.inOut',
        onComplete: function () { window.location.href = link.href; }
      });
    });
  }

  /* ====================================================================== */
  /* Boot                                                                   */
  /* ====================================================================== */
  function boot() {
    initSmoothScroll();
    bridgeOverlayScrollLocks();
    initCursor();
    initPageTransitions();

    // Build the scroll scenes first so ScrollTrigger measures a settled
    // layout, then run the intro, then release scroll.
    initHero();
    initStories();
    initMagnetic();
    initReveals();

    initIntro(function () {
      ScrollTrigger.refresh();
    });

    // Images finishing late shift every trigger's start position.
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
